import React from "react";
import "@fontsource/jetbrains-mono";
import "typeface-rubik";

import { LoginView, Rebase, RebaseAuth, useRebaseAuthController } from "@rebasepro/app";
import { RebaseCMS, RebaseShell } from "@rebasepro/cms";
import type { AuthControllerExtended } from "@rebasepro/cms-types";
import { useDataEnhancementPlugin } from "@rebasepro/plugin-ai";
import { RebaseStudio } from "@rebasepro/studio";
import { createRebaseClient } from "@rebasepro/client";
import { collections } from "virtual:rebase-collections";
import { ExtractJobAction } from "../../config/collections/actions/ExtractJobAction";
import { ImportCvAction } from "../../config/collections/actions/ImportCvAction";
import { ApplicantsPanel } from "../../config/views/ApplicantsPanel";
import { UserTalentPanel } from "../../config/views/UserTalentPanel";
import { QuestionnairePanel } from "../../config/views/QuestionnairePanel";
import { HomeView } from "../../config/views/HomeView";
import { ScrapedReviewView } from "../../config/views/ScrapedReviewView";
import { ProspectsView } from "../../config/views/ProspectsView";
import { TalentsQueue } from "../../config/views/TalentsQueue";
import { TalentOverviewTab } from "../../config/views/TalentOverview";
import { JobOverview } from "../../config/views/JobOverview";
import { CompanyOverview } from "../../config/views/CompanyOverview";
import { InboxView } from "../../config/views/InboxView";
import { EntityEmailPanel } from "../../config/views/EntityEmailPanel";
import { InboxAutoSync } from "../../config/views/useInboxAutoSync";

// Register frontend-only Actions on collections (Actions are React components
// and must not live in the shared config package that the backend also imports).
// Since 0.11 both live under the collection's `admin` block — the backend loads
// that block and never reads inside it, which is what keeps these out of its way.
//
// The "Matches" tab that used to be here ranked every candidate in the database
// against this vacancy and wrote its verdicts to a table parallel to the real
// one. `ApplicantsPanel` is the readable half of it aimed at the only population
// it may describe — the people who applied — and its buttons move the
// application's own status rather than an opinion recorded elsewhere.
const jobsCollection = collections.find((c) => c.slug === "jobs");
if (jobsCollection) {
    jobsCollection.admin = {
        ...(jobsCollection.admin ?? {}),
        Actions: [ExtractJobAction],
        entityViews: [
            ...(jobsCollection.admin?.entityViews ?? []),
            // The cover, and the tab a vacancy opens on — see
            // `JobOverview`, and `talents` for the same pattern. Eighty
            // properties in declaration order is the screen for correcting a
            // posting, not the one for reading it.
            {
                key: "resumen",
                name: "Resumen",
                position: "start",
                Builder: JobOverview
            },
            {
                key: "postulaciones",
                name: "Postulaciones",
                Builder: ApplicantsPanel
            }
        ]
    };
}

/**
 * "Correo" on a candidate and on a company.
 *
 * The mailbox as a whole lives at `/correo`; this is the same correspondence
 * filtered to the record you are looking at, because "what have we said to *this*
 * person" is asked while looking at them. An operator who has to leave the
 * record, find the thread among four hundred and come back has been handed a
 * worse version of Gmail.
 *
 * The two differ only in where the address comes from: a candidate has one, and
 * a company's is on its member rows rather than on the company itself — so the
 * company tab opens with an empty "Para" and the operator or the drafter fills
 * it. Better than guessing at a shared mailbox from the domain, which is exactly
 * the kind of invented address `prospect_contacts.email_status` exists to refuse.
 */
/**
 * "Candidato" on a user account.
 *
 * The link between `talents` and `rebase.users` only ever read one way: from a
 * candidate you could see the account, and from the account nothing pointed
 * back — the users collection has no column to hold it, and a relation
 * property cannot invent one. The panel runs the reverse lookup instead.
 *
 * Worth having now that drizzle/0048 makes the link a foreign key: the database
 * guarantees a candidate's account exists, and this is where an operator can
 * see, from the account, whether it is a candidate at all.
 */
const usersCollection = collections.find((c) => c.slug === "users");
if (usersCollection) {
    usersCollection.admin = {
        ...(usersCollection.admin ?? {}),
        entityViews: [
            ...(usersCollection.admin?.entityViews ?? []),
            {
                key: "candidato",
                name: "Candidato",
                Builder: UserTalentPanel
            }
        ]
    };
}

const talentsCollection = collections.find((c) => c.slug === "talents");
if (talentsCollection) {
    talentsCollection.admin = {
        ...(talentsCollection.admin ?? {}),
        Actions: [ImportCvAction],
        entityViews: [
            ...(talentsCollection.admin?.entityViews ?? []),
            /**
             * The cover, and the tab a candidate opens on.
             *
             * The same component the "Cola" view mode draws in its detail pane —
             * see `TalentOverview` — so the person you were just looking at in
             * the queue does not become forty form fields the moment you open
             * their record. `defaultSelectedView` in `collections/talents.ts`
             * names this key; the form, the questionnaire and the
             * correspondence are the tabs beside it.
             *
             * `position: "start"` is what puts it ahead of the record's own
             * tab. It used to mean only "first among the custom views", which
             * placed it after a tab it was already after; rebase now draws the
             * "start" views before the record, which is the only reading of the
             * word that does anything.
             */
            {
                key: "resumen",
                name: "Resumen",
                position: "start",
                Builder: TalentOverviewTab
            },
            // The questionnaire is twenty-odd fields the candidate answered on
            // the public site and nobody edits here. As a form block it is a
            // screen you fill in; as a tab it is the answers, grouped the way
            // the wizard asked them, with the unanswered ones out of the way
            // rather than taking a row each. The form block stays — this is the
            // reading surface, not a replacement for editing.
            {
                key: "cuestionario",
                name: "Cuestionario",
                Builder: QuestionnairePanel
            },
            {
                key: "correo",
                name: "Correo",
                Builder: ({ entity }: any) => (
                    <EntityEmailPanel
                        kind="talent"
                        entityId={entity?.id ?? ""}
                        email={entity?.values?.email ?? null}
                        displayName={entity?.values?.full_name ?? null}
                    />
                )
            }
        ]
    };
}

const companiesCollection = collections.find((c) => c.slug === "companies");
if (companiesCollection) {
    companiesCollection.admin = {
        ...(companiesCollection.admin ?? {}),
        entityViews: [
            ...(companiesCollection.admin?.entityViews ?? []),
            // The cover. Reads the three questions an employer's record is
            // opened to answer — are they hiring with us, are we working them,
            // have we spoken — of which the middle one lives under a different
            // key in a different table. See `CompanyOverview`.
            {
                key: "resumen",
                name: "Resumen",
                position: "start",
                Builder: CompanyOverview
            },
            {
                key: "correo",
                name: "Correo",
                Builder: ({ entity }: any) => (
                    <EntityEmailPanel
                        kind="company"
                        entityId={entity?.id ?? ""}
                        // `companies` has no contact address of its own — the
                        // people are in `company_members` — so this opens empty
                        // rather than inventing `info@` from the domain.
                        email={null}
                        displayName={entity?.values?.name ?? null}
                        // The outreach filed against this same employer under
                        // its prospect key. Without it the tab showed a company
                        // as never contacted while a months-old thread with them
                        // sat in "Empresas que contratan" — see 0044.
                        employerKey={entity?.values?.employer_key ?? null}
                    />
                )
            }
        ]
    };
}

// Configuration from environment
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? "http://localhost:3001" : window.location.origin);
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Who gets the schema editor.
 *
 * The operating team signs in as @sustentalent.com; the people who change
 * the schema sign in from the developer's own domain. A domain rather than a
 * list of addresses, so a new developer needs no code change and a new
 * operator gets no switch. `VITE_STUDIO_DOMAINS` overrides the default.
 */
function isDeveloper(authController: { user?: { email?: string | null } | null }): boolean {
    if (import.meta.env.DEV) return true;
    const domains = String(import.meta.env.VITE_STUDIO_DOMAINS || "firecms.co")
        .split(",").map((d: string) => d.trim().toLowerCase()).filter(Boolean);
    const email = (authController.user?.email ?? "").toLowerCase();
    const at = email.lastIndexOf("@");
    return at > 0 && domains.includes(email.slice(at + 1));
}

/**
 * One-time: forget a drawer the operator collapsed before it had labels.
 *
 * Rebase remembers the drawer's state in localStorage and that memory beats
 * `defaultDrawerOpen` on every load, so the people this change is for — the
 * ones who had already collapsed it — would never see it. Cleared once, under
 * a key that says it happened, so a later deliberate collapse is kept.
 */
function resetDrawerOnce(): void {
    if (typeof window === "undefined") return;
    try {
        const done = "sustentalent-drawer-reset:2026-09";
        if (window.localStorage.getItem(done)) return;
        for (const key of Object.keys(window.localStorage)) {
            if (key.startsWith("rebase-drawer-open:")) window.localStorage.removeItem(key);
        }
        window.localStorage.setItem(done, "1");
    } catch {
        // Storage unavailable: the drawer opens by its default, which is now open.
    }
}
resetDrawerOnce();

export function App() {
    const rebaseClient = React.useMemo(() => createRebaseClient({
        baseUrl: API_URL
    }), []);

    const authController = useRebaseAuthController({
        client: rebaseClient,
        googleClientId: GOOGLE_CLIENT_ID
    });

    const dataEnhancementPlugin = useDataEnhancementPlugin();

    return (
        <Rebase
            client={rebaseClient}
            authController={authController}
            plugins={[dataEnhancementPlugin]}
            // The collections, the custom views and the product itself are all
            // in Spanish; without this the surrounding chrome ("Add", "Table",
            // "Filters", "Search") stays English and the panel reads as two
            // half-translated products stitched together.
            locale="es"
        >
            {/*
                The sign-in screen, for the logo alone.

                Left to itself the gate renders `<LoginView>` with no `logo`,
                and its fallback is Rebase's own rainbow cube — the first
                screen of this product showed another company's mark. Nothing
                else here differs from the default view; `authController` is
                the same one the gate would have passed.

                `logo-login.svg`, not `logo-symbol.svg`: the card draws the
                mark into a 96x96 box with `objectFit: cover`, and the symbol
                is portrait, so it came back cropped at both ends. Same
                artwork, square canvas.
            */}
            <RebaseAuth loginView={<LoginView authController={authController as AuthControllerExtended} logo="/logo-login.svg" />} />
            <RebaseCMS
                collections={collections}
                // Replaces the default collection-grid landing page.
                homePage={<HomeView />}
                // The drawer is the only map of this product, and it was named
                // after nothing anyone could point at. "Reclutamiento" held six
                // entries — a dashboard, two custom views and three tables —
                // while "Captación" held one real table and one empty one, and
                // the split between them was a distinction only this repo makes.
                //
                // Groups are now the three nouns the business actually has:
                // offers, people, companies. Every entry sits under the noun it
                // is about, so the group answers "what kind of thing is this"
                // before the entry has to. Each entry name still stands alone,
                // because the drawer collapses to a rail of icons whose only
                // label is the entry's own name in a tooltip.
                //
                // Order within a group is the order this list gives, and it runs
                // from the queue you work to the archive you consult.
                //
                // No `icon` on any group: `NavigationGroupMapping.icon` is
                // optional and the header draws one only when it is set. The
                // entries carry icons because they are targets you click; a
                // group header is a label, and giving it one put a second glyph
                // column beside the first that pointed at nothing.
                navigationGroupMappings={[
                    {
                        // "Oferta" and "vacante" are the same thing in a
                        // recruiter's mouth, and this drawer had one of each,
                        // one above the other, as if they were different kinds
                        // of record. One noun.
                        name: "Vacantes",
                        entries: ["ofertas-por-revisar", "jobs"]
                    },
                    {
                        // One entry, where there were two. "Candidatos" (the
                        // queue) and `talents` (the table) were both here and
                        // both described the same ~300 people; the queue is now
                        // a view mode of the collection rather than a screen
                        // beside it, so there is one thing to click and a view
                        // switcher inside it. See `collections/talents.ts`.
                        //
                        // `talent-applications` is `hideFromNavigation` and was
                        // listed here anyway for a while — the entry never
                        // rendered, which is how a name in this array quietly
                        // stops meaning anything.
                        // Two entries since the newsletter shipped. A
                        // subscription is not a view of a candidate — it is a
                        // standing permission to write to them, and the
                        // question it answers ("who is on the list, and what do
                        // they get") is asked about the list and not about a
                        // person. `digest-sends` is the receipts and stays
                        // `hideFromNavigation`: listing it here would render
                        // nothing, which is how a name in this array quietly
                        // stops meaning anything.
                        name: "Personas",
                        entries: ["talents", "digest-subscriptions"]
                    },
                    {
                        // Renamed from "Empresas y contacto", which described the
                        // rows rather than the work and so collected anything
                        // company-shaped: the prospecting queue, the brand
                        // directory that powers the public board, the inbound
                        // leads and a lead-magnet table with nothing in it. Four
                        // different jobs under one heading, two of them
                        // operational and two commercial.
                        //
                        // "Marketing" is what these three actually are: finding
                        // employers who are hiring, and handling the ones who
                        // came to us. `companies` moved out — it is the employer
                        // directory behind the vacancies, reference data for the
                        // board rather than something anybody prospects.
                        //
                        // One entry since 2026-09-06. `company-leads` and
                        // `contact-messages` were listed beside it and are the
                        // raw tables it reads: an employer who wrote to us
                        // appeared in three places and none of them said which
                        // was the door. Both are `hideFromNavigation` now and
                        // still reachable by URL.
                        // Not "Marketing": nobody on this screen does
                        // marketing. They find employers who are hiring and
                        // write to them.
                        name: "Captación",
                        entries: ["empresas-que-contratan"]
                    },
                    {
                        // `companies` had to go somewhere, and until now it went
                        // nowhere: it kept declaring `group: "Empresas y
                        // contacto"` after that group was renamed, and a
                        // collection that names a group absent from this array
                        // still renders — as its own heading. So the drawer grew
                        // a second company section holding one entry, which is
                        // the drift this array exists to prevent.
                        //
                        // Its own group and not folded into Marketing, because
                        // it is not marketing: it is the employer directory the
                        // public board reads for logos and brand colours. Rows
                        // arrive here by enrichment, not by anybody deciding to
                        // pursue them.
                        // "Empresas", not "Directorio", which named nothing.
                        // The entry inside carries the distinction: these are
                        // the records, and "Empresas que contratan" above is
                        // the work.
                        name: "Empresas",
                        entries: ["companies"]
                    },
                    {
                        // Its own group, and not under "Empresas y contacto",
                        // because the mailbox is not about companies: it holds
                        // conversations with candidates, employers and people
                        // who are neither. Filing it under one of the three
                        // nouns would say it belongs to that one.
                        //
                        // One entry, deliberately. `email-threads` and
                        // `email_messages` are collections so the data plane can
                        // reach them, but a raw table view of either would be a
                        // second, much worse inbox — so neither is listed here.
                        name: "Correo",
                        entries: ["correo"]
                    },
                    // "Ajustes", not "Settings". Rebase sinks a group to the
                    // bottom of the drawer on that literal name, which is why it
                    // was left in English — and it read as the one untranslated
                    // word in a Spanish panel. Listing it last here puts it last
                    // by position instead, since group order follows this array.
                    {
                        name: "Ajustes",
                        entries: ["users"]
                    }
                ]}
                views={[
                    // "Hoy" is the home page and nothing else. It was also a
                    // drawer entry, on the reasoning that the logo is not an
                    // obvious way back — but that made one screen appear twice
                    // in the navigation under two routes, which is its own kind
                    // of confusing. `homePage` above is the registration; the
                    // logo is the way back.
                    //
                    // Renamed from "Ofertas externas", which said where the rows
                    // came from and not what you are meant to do with them —
                    // and read as a sibling of "Vacantes" rather than as the
                    // queue that feeds it. The slug moves with the name so the
                    // URL is not a third name for the same screen; nothing
                    // outside this file links to the old one.
                    {
                        slug: "ofertas-por-revisar",
                        name: "Por revisar",
                        description: "Vacantes que encontramos publicadas en otras webs de empleo. Aprobar una la copia a nuestro tablón.",
                        icon: "CloudDownload",
                        group: "Vacantes",
                        view: ScrapedReviewView,
                        roles: ["admin"]
                    },
                    // The same `scraped_jobs` rows the queue above reviews one
                    // at a time, read down the other axis: who posted them. That
                    // turns a review queue into the list of companies hiring for
                    // sustainability right now, which is the only prospect list
                    // this business has that nobody had to build.
                    {
                        slug: "empresas-que-contratan",
                        name: "Empresas que contratan",
                        description: "Empresas a las que hemos visto publicando vacantes de sostenibilidad, y a quién hemos escrito.",
                        icon: "ContactRound",
                        // Was "Empresas y contacto" — a group that no longer
                        // exists in `navigationGroupMappings` above. The mapping
                        // put the entry under Marketing anyway, so nothing was
                        // visibly broken and the stale string sat there naming a
                        // section nobody could see.
                        group: "Captación",
                        view: ProspectsView,
                        roles: ["admin"]
                    },
                    // "Candidatos" was here, as a top-level view. It is now the
                    // "Cola" view mode of the `talents` collection instead —
                    // same component, registered in `collectionViews` below.
                    // As an `AppView` it was a second door to a table that
                    // already had one, and it had to re-implement a title, a
                    // reload button and a search box that the collection
                    // toolbar draws for free.
                    //
                    // The mailbox. Everything this business does with a person
                    // outside the product happens in email, and until this
                    // screen existed none of it was in the panel: it could say
                    // an employer had four contacts and that somebody clicked
                    // "Escribir", and nothing about what was written or what
                    // came back.
                    {
                        slug: "correo",
                        name: "Correo",
                        description: "Lo que se ha escrito y lo que han contestado, junto a la ficha de quien lo dijo.",
                        icon: "Mail",
                        group: "Correo",
                        view: InboxView,
                        roles: ["admin"]
                    },
                    // There is no third view. "Matching" was here, and it is gone
                    // along with the rest of that feature: a screen that paired
                    // vacancies with candidates who had not applied to them
                    // invented a relationship neither side had agreed to, and it
                    // sat in the drawer looking exactly like "Postulaciones",
                    // which records one that actually happened.
                ]}
                // Custom renderings of a collection's own rows, named by key
                // from a collection's `admin.customViews`. Registered here
                // rather than inline in the collection for the reason every
                // other React member of that block is: `config/collections/*`
                // is loaded by the backend for its schema, and a component
                // import there drags the admin bundle in with it.
                collectionViews={[
                    {
                        key: "cola",
                        name: "Cola",
                        icon: "ListChecks",
                        Builder: TalentsQueue
                        // `openEntityMode` was "side_panel" here — a record over
                        // the queue rather than replacing it. The collection's
                        // own `openEntityMode` outranks a view's in rebase, and
                        // `talents` now says "full_screen" (see the collection),
                        // so a value here would be a statement with no effect.
                        // "Ficha completa" opens the record as a page and the
                        // browser's back returns to the queue.
                    }
                ]}
                collectionEditor={{
                    getAuthToken: authController.getAuthToken
                }}
            />
            {/*
                Studio is the schema editor: tables, columns, policies. It
                put a "CMS | Studio" switch at the top of every operator's
                drawer, one click from editing the database. The people who
                run the panel work queues; the people who change the schema
                work in this repository. Shown to the second group only —
                developer accounts by domain, and any local build.
            */}
            {isDeveloper(authController) && <RebaseStudio />}
            {/* The mark on its own, not the full lockup: the drawer draws the
                logo at 28px directly beside the word "SustenTalent", so
                `public/logo.svg` — which is symbol *and* wordmark — would put
                an illegible second copy of the title next to the real one.
                `logo-symbol.svg` is the same artwork cropped to the leaf. */}
            {/*
                Reads the mailbox once a minute while this tab is open and
                visible, so a reply shows up without anybody pressing anything.
                Renders nothing.

                Mounted here rather than inside the "Correo" screen on purpose:
                somebody working through the vacancy queue still wants the unread
                count to be true and a reply to be waiting when they get to it.

                Gated on the admin role because `sync-inbox` is admin-only and
                would otherwise answer 403 to a non-admin once a minute forever.
                The server's own floor (INBOX_MIN_POLL_SECONDS) is what actually
                bounds how often Gmail is contacted — see useInboxAutoSync.tsx.
            */}
            <InboxAutoSync enabled={Boolean((authController as any)?.user?.roles?.includes?.("admin"))} />
            {/*
                Open, not a rail of icons. The drawer is the only map of this
                product, and collapsed it is ten glyphs with no names, two
                pairs of them identical — the first reason an operator says
                "I don't know what I'm supposed to do here" is not knowing
                where they are. `defaultDrawerOpen` seeds the first visit
                only; `resetDrawerOnce` below clears the stored collapse for
                everyone who had already learned to live with the rail.
            */}
            <RebaseShell title="SustenTalent" logo="/logo-symbol.svg" defaultDrawerOpen />
        </Rebase>
    );
}
